/**
 * ProjectSections.tsx - Components for project sections
 * Inspired by Teamwork.com
 */

import React, { useState, useMemo } from 'react';
import {
  Plus,
  MoreHorizontal,
  Search,
  Filter,
  ChevronRight,
  ChevronDown,
  GripVertical,
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  User,
  Users,
  Mail,
  Phone,
  Calendar,
  FileText,
  Link as LinkIcon,
  ExternalLink,
  MessageSquare,
  Edit2,
  Trash2,
  Star,
  StarOff,
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  Wallet,
  Target,
  Flag,
  ArrowRight,
  Send,
  Paperclip,
  Image,
  Download,
  Eye,
  MoreVertical,
  Copy,
  Share2,
  Archive,
  Tag,
  Folder,
  X,
} from 'lucide-react';
import type { Task, TaskStatus, TeamMember, TimeEntry } from '@business-assistant/shared/domain';
import { useTranslation } from '../../hooks';
import {
  MemberWidget,
  NotebookWidget,
  LinkWidget,
  RiskWidget,
  TicketWidget,
  MessageDetail,
  ProofWidget,
  FormBuilderWidget,
  formStyles,
  type MemberFormData,
  type NotebookFormData,
  type LinkFormData,
  type RiskFormData,
  type TicketFormData,
  type MessageData,
  type ProofFormData,
  type CustomFormData,
} from './ProjectForms';

// Simple toast notification
const showToast = (message: string) => {
  // Create toast element
  const toast = document.createElement('div');
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    z-index: 9999;
    animation: fadeIn 0.2s ease-out;
  `;
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.2s';
    setTimeout(() => toast.remove(), 200);
  }, 2000);
};

// =============================================================================
// TYPES
// =============================================================================

interface KanbanColumn {
  id: TaskStatus;
  title: string;
  color: string;
  tasks: Task[];
}

interface ProjectMember {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  avatar?: string;
  department?: string;
  tasksCount: number;
  hoursLogged: number;
}

interface ProjectMessage {
  id: string;
  author: { name: string; avatar?: string };
  content: string;
  createdAt: string;
  replies?: ProjectMessage[];
  attachments?: { name: string; size: string }[];
  isPinned?: boolean;
}

interface ProjectNotebook {
  id: string;
  title: string;
  content: string;
  author: string;
  updatedAt: string;
  category?: string;
}

interface ProjectLink {
  id: string;
  title: string;
  url: string;
  description?: string;
  category?: string;
  addedBy: string;
  addedAt: string;
  favicon?: string;
}

interface ActivityItem {
  id: string;
  type: 'task_created' | 'task_completed' | 'comment' | 'file_uploaded' | 'member_added' | 'milestone_reached' | 'time_logged';
  actor: string;
  target?: string;
  description: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

interface Risk {
  id: string;
  title: string;
  description: string;
  probability: 'low' | 'medium' | 'high';
  impact: 'low' | 'medium' | 'high';
  status: 'identified' | 'mitigating' | 'resolved' | 'accepted';
  owner: string;
  mitigation?: string;
  createdAt: string;
}

// =============================================================================
// MOCK DATA
// =============================================================================

const MOCK_MEMBERS: ProjectMember[] = [
  { id: '1', name: 'Marco Rossi', email: 'marco@example.com', role: 'owner', department: 'Management', tasksCount: 12, hoursLogged: 45.5 },
  { id: '2', name: 'Laura Bianchi', email: 'laura@example.com', role: 'admin', department: 'Design', tasksCount: 8, hoursLogged: 32 },
  { id: '3', name: 'Giuseppe Verdi', email: 'giuseppe@example.com', role: 'member', department: 'Development', tasksCount: 15, hoursLogged: 67 },
  { id: '4', name: 'Anna Neri', email: 'anna@example.com', role: 'member', department: 'Development', tasksCount: 10, hoursLogged: 41 },
  { id: '5', name: 'Paolo Ferrari', email: 'paolo@example.com', role: 'viewer', department: 'Sales', tasksCount: 3, hoursLogged: 8 },
];

const MOCK_MESSAGES: ProjectMessage[] = [
  {
    id: '1',
    author: { name: 'Marco Rossi' },
    content: 'I just uploaded the new project specifications. Please review by Friday.',
    createdAt: '2024-01-15T10:30:00',
    isPinned: true,
    attachments: [{ name: 'specifiche_v2.pdf', size: '2.4 MB' }],
    replies: [
      { id: '1-1', author: { name: 'Laura Bianchi' }, content: "Perfect, I'll check them this afternoon!", createdAt: '2024-01-15T11:15:00' },
    ],
  },
  {
    id: '2',
    author: { name: 'Giuseppe Verdi' },
    content: 'The staging deploy is complete. We can start integration testing.',
    createdAt: '2024-01-14T16:45:00',
  },
  {
    id: '3',
    author: { name: 'Anna Neri' },
    content: 'I noticed an issue with the authentication module. Opening a ticket to track it.',
    createdAt: '2024-01-14T09:20:00',
  },
];

const MOCK_NOTEBOOKS: ProjectNotebook[] = [
  { id: '1', title: 'System Architecture', content: 'Technical documentation...', author: 'Giuseppe Verdi', updatedAt: '2024-01-10', category: 'Technical' },
  { id: '2', title: 'User Guide', content: 'How to use...', author: 'Laura Bianchi', updatedAt: '2024-01-12', category: 'Documentation' },
  { id: '3', title: 'API Reference', content: 'Available endpoints...', author: 'Anna Neri', updatedAt: '2024-01-08', category: 'Technical' },
  { id: '4', title: 'Meeting Notes - Kickoff', content: 'Decisions made...', author: 'Marco Rossi', updatedAt: '2024-01-05', category: 'Meeting' },
];

const MOCK_LINKS: ProjectLink[] = [
  { id: '1', title: 'GitHub Repository', url: 'https://github.com/example/project', description: 'Project source code', category: 'Development', addedBy: 'Giuseppe Verdi', addedAt: '2024-01-05' },
  { id: '2', title: 'Figma Design', url: 'https://figma.com/file/xxx', description: 'Design system and mockups', category: 'Design', addedBy: 'Laura Bianchi', addedAt: '2024-01-06' },
  { id: '3', title: 'API Documentation', url: 'https://api-docs.example.com', description: 'Swagger API documentation', category: 'Development', addedBy: 'Anna Neri', addedAt: '2024-01-07' },
  { id: '4', title: 'Jira Board', url: 'https://jira.example.com/board', description: 'Issue tracking', category: 'Management', addedBy: 'Marco Rossi', addedAt: '2024-01-05' },
];

const MOCK_ACTIVITY: ActivityItem[] = [
  { id: '1', type: 'task_completed', actor: 'Giuseppe Verdi', target: 'Implement OAuth login', description: 'completed the task', timestamp: '2024-01-15T14:30:00' },
  { id: '2', type: 'comment', actor: 'Laura Bianchi', target: 'Design homepage', description: 'commented', timestamp: '2024-01-15T12:15:00' },
  { id: '3', type: 'file_uploaded', actor: 'Marco Rossi', target: 'specifiche_v2.pdf', description: 'uploaded', timestamp: '2024-01-15T10:30:00' },
  { id: '4', type: 'time_logged', actor: 'Anna Neri', description: 'logged 3h 30m', timestamp: '2024-01-14T18:00:00', metadata: { hours: 3.5 } },
  { id: '5', type: 'member_added', actor: 'Marco Rossi', target: 'Paolo Ferrari', description: 'added', timestamp: '2024-01-14T09:00:00' },
  { id: '6', type: 'milestone_reached', actor: 'Team', target: 'MVP Release', description: 'reached the milestone', timestamp: '2024-01-13T17:00:00' },
];

const MOCK_RISKS: Risk[] = [
  { id: '1', title: 'External API delivery delay', description: 'The vendor may not meet the timeline', probability: 'medium', impact: 'high', status: 'mitigating', owner: 'Marco Rossi', mitigation: 'Prepare mock APIs to continue development', createdAt: '2024-01-10' },
  { id: '2', title: 'Insufficient budget', description: 'Unexpected costs may exceed the budget', probability: 'low', impact: 'high', status: 'identified', owner: 'Marco Rossi', createdAt: '2024-01-08' },
  { id: '3', title: 'Team turnover', description: 'Possible departure of key members', probability: 'low', impact: 'medium', status: 'accepted', owner: 'Marco Rossi', createdAt: '2024-01-05' },
];

interface Ticket {
  id: string;
  title: string;
  description: string;
  type: 'bug' | 'feature' | 'support' | 'question';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in-progress' | 'waiting' | 'resolved' | 'closed';
  assignee?: string;
  createdAt: string;
  reporter: string;
}

const MOCK_TICKETS: Ticket[] = [
  { id: 'tkt-001', title: 'Login not working on Safari', description: 'Safari users are unable to log in', type: 'bug', priority: 'high', status: 'in-progress', assignee: 'Giuseppe Verdi', createdAt: '2024-01-14', reporter: 'Cliente XYZ' },
  { id: 'tkt-002', title: 'Add PDF export', description: 'Request to export reports in PDF format', type: 'feature', priority: 'medium', status: 'open', createdAt: '2024-01-13', reporter: 'Anna Neri' },
  { id: 'tkt-003', title: 'How to reset password?', description: 'A user asks about password reset', type: 'support', priority: 'low', status: 'resolved', assignee: 'Laura Bianchi', createdAt: '2024-01-12', reporter: 'Mario Rossi' },
];

interface Proof {
  id: string;
  title: string;
  description?: string;
  fileUrl?: string;
  fileName?: string;
  status: 'pending' | 'approved' | 'rejected' | 'changes-requested';
  reviewers: string[];
  uploadedAt: string;
  uploadedBy: string;
}

const MOCK_PROOFS: Proof[] = [
  { id: '1', title: 'Homepage Design v2', description: 'New homepage design with updated hero section', fileName: 'homepage_v2.pdf', status: 'pending', reviewers: ['Marco Rossi', 'Anna Neri'], uploadedAt: '2024-01-15', uploadedBy: 'Laura Bianchi' },
  { id: '2', title: 'Final company logo', description: 'Final version of the logo with all variants', fileName: 'logo_final.ai', status: 'approved', reviewers: ['Marco Rossi'], uploadedAt: '2024-01-10', uploadedBy: 'Laura Bianchi' },
  { id: '3', title: 'Product brochure', fileName: 'brochure_draft.pdf', status: 'changes-requested', reviewers: ['Marco Rossi', 'Paolo Ferrari'], uploadedAt: '2024-01-08', uploadedBy: 'Laura Bianchi' },
];

interface CustomForm {
  id: string;
  title: string;
  description?: string;
  fieldsCount: number;
  responsesCount: number;
  createdAt: string;
  createdBy: string;
  status: 'draft' | 'active' | 'closed';
}

const MOCK_FORMS: CustomForm[] = [
  { id: '1', title: 'Leave request', description: 'Form to request vacation days', fieldsCount: 5, responsesCount: 12, createdAt: '2024-01-05', createdBy: 'Marco Rossi', status: 'active' },
  { id: '2', title: 'Project feedback', description: 'Collect feedback from the team about the project', fieldsCount: 8, responsesCount: 7, createdAt: '2024-01-10', createdBy: 'Marco Rossi', status: 'active' },
  { id: '3', title: 'Sondaggio cliente', description: 'Questionario di soddisfazione cliente', fieldsCount: 10, responsesCount: 0, createdAt: '2024-01-14', createdBy: 'Anna Neri', status: 'draft' },
];

// =============================================================================
// SHARED STYLES
// =============================================================================

const sectionStyles = {
  container: {
    background: 'var(--bg-base)',
    borderRadius: '0.75rem',
    border: '1px solid var(--line)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.25rem',
    borderBottom: '1px solid var(--line)',
    background: 'var(--bg-light)',
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  content: {
    padding: '1rem',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 1.5rem',
    textAlign: 'center' as const,
    color: 'var(--text-muted)',
  },
  card: {
    background: 'var(--bg-base)',
    border: '1px solid var(--line)',
    borderRadius: '0.5rem',
    padding: '1rem',
    marginBottom: '0.75rem',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.25rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.875rem',
    borderRadius: '0.375rem',
    border: 'none',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  buttonPrimary: {
    background: 'var(--brand)',
    color: 'white',
  },
  buttonSecondary: {
    background: 'var(--bg-light)',
    color: 'var(--text-primary)',
    border: '1px solid var(--line)',
  },
  searchInput: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.375rem',
    border: '1px solid var(--line)',
    background: 'var(--bg-base)',
    fontSize: '0.875rem',
  },
};

// =============================================================================
// 1. BOARD (KANBAN) SECTION
// =============================================================================

interface BoardSectionProps {
  tasks: Task[];
  onMoveTask: (taskId: string, newStatus: TaskStatus) => void;
  onTaskClick: (taskId: string) => void;
  onAddTask: () => void;
}

export function BoardSection({ tasks, onMoveTask, onTaskClick, onAddTask }: BoardSectionProps) {
  const { t } = useTranslation();
  const [draggedTask, setDraggedTask] = useState<string | null>(null);

  const columns: KanbanColumn[] = useMemo(() => [
    { id: 'todo', title: t('projects.taskStatus.todo'), color: '#6b7280', tasks: tasks.filter(t => t.status === 'todo') },
    { id: 'doing', title: t('projects.taskStatus.doing'), color: '#3b82f6', tasks: tasks.filter(t => t.status === 'doing') },
    { id: 'done', title: t('projects.taskStatus.done'), color: '#22c55e', tasks: tasks.filter(t => t.status === 'done') },
  ], [tasks, t]);

  const handleDragStart = (taskId: string) => {
    setDraggedTask(taskId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (columnId: TaskStatus) => {
    if (draggedTask) {
      onMoveTask(draggedTask, columnId);
      setDraggedTask(null);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '1rem', overflowX: 'auto', padding: '0.5rem' }}>
      {columns.map(column => (
        <div
          key={column.id}
          style={{
            flex: '0 0 320px',
            background: 'var(--bg-light)',
            borderRadius: '0.75rem',
            display: 'flex',
            flexDirection: 'column',
          }}
          onDragOver={handleDragOver}
          onDrop={() => handleDrop(column.id)}
        >
          {/* Column Header */}
          <div style={{
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '2px solid',
            borderColor: column.color,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: column.color,
              }} />
              <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{column.title}</span>
              <span style={{
                background: 'var(--bg-base)',
                padding: '0.125rem 0.5rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
              }}>
                {column.tasks.length}
              </span>
            </div>
            <button
              onClick={onAddTask}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                color: 'var(--text-muted)',
              }}
            >
              <Plus size={18} />
            </button>
          </div>

          {/* Column Tasks */}
          <div style={{ padding: '0.75rem', flex: 1, overflowY: 'auto', minHeight: '200px' }}>
            {column.tasks.map(task => (
              <div
                key={task.id}
                draggable
                onDragStart={() => handleDragStart(task.id)}
                onClick={() => onTaskClick(task.id)}
                style={{
                  background: 'var(--bg-base)',
                  borderRadius: '0.5rem',
                  padding: '0.875rem',
                  marginBottom: '0.5rem',
                  cursor: 'grab',
                  border: '1px solid var(--line)',
                  boxShadow: draggedTask === task.id ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                  opacity: draggedTask === task.id ? 0.5 : 1,
                  transition: 'box-shadow 0.15s, opacity 0.15s',
                }}
              >
                <div style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  {task.title}
                </div>
                {task.dueAt && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    fontSize: '0.75rem',
                    color: new Date(task.dueAt) < new Date() ? 'var(--error)' : 'var(--text-muted)',
                  }}>
                    <Calendar size={12} />
                    {new Date(task.dueAt).toLocaleDateString(undefined)}
                  </div>
                )}
                {task.assignees && task.assignees.length > 0 && (
                  <div style={{ display: 'flex', marginTop: '0.5rem' }}>
                    {task.assignees.slice(0, 3).map((_, i) => (
                      <div
                        key={i}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: ['#3b82f6', '#22c55e', '#f59e0b'][i % 3],
                          border: '2px solid var(--bg-base)',
                          marginLeft: i > 0 ? '-8px' : 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.625rem',
                          fontWeight: 600,
                        }}
                      >
                        {task.assignees?.[i]?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {column.tasks.length === 0 && (
              <div style={{
                padding: '2rem 1rem',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: '0.8125rem',
              }}>
                {t('projects.kanban.noTasks')}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// 2. PEOPLE SECTION
// =============================================================================

interface PeopleSectionProps {
  projectId: string;
  onAddMember?: () => void;
}

export function PeopleSection({ projectId, onAddMember }: PeopleSectionProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState<ProjectMember | null>(null);
  const members = MOCK_MEMBERS;

  const handleMemberClick = (member: ProjectMember) => {
    setEditingMember(member);
    setShowForm(true);
  };

  const handleAddMember = () => {
    setEditingMember(null);
    setShowForm(true);
  };

  const handleSaveMember = (data: MemberFormData) => {
    console.log('Save member:', data);
    setShowForm(false);
    setEditingMember(null);
  };

  const handleDeleteMember = (id: string) => {
    console.log('Delete member:', id);
    setShowForm(false);
    setEditingMember(null);
  };

  const filteredMembers = members.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const roleLabels: Record<string, string> = {
    owner: t('projects.roles.owner'),
    admin: t('projects.roles.admin'),
    member: t('projects.roles.member'),
    viewer: t('projects.roles.viewer'),
  };

  const roleColors: Record<string, string> = {
    owner: '#8b5cf6',
    admin: '#3b82f6',
    member: '#22c55e',
    viewer: '#6b7280',
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <Users size={18} />
          {t('projects.sections.people')}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({members.length})</span>
        </div>
        <div style={sectionStyles.headerActions}>
          <div style={sectionStyles.searchInput}>
            <Search size={16} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder={t('projects.searchMembers')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ border: 'none', outline: 'none', background: 'transparent', width: '150px' }}
            />
          </div>
          <button
            onClick={handleAddMember}
            style={{ ...sectionStyles.button, ...sectionStyles.buttonPrimary }}
          >
            <Plus size={16} /> {t('common.create')}
          </button>
        </div>
      </div>

      <div style={{ padding: '1rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--line)' }}>
              <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('projects.member')}</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('projects.role')}</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('projects.department')}</th>
              <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('tasks.task')}</th>
              <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{t('projects.hours')}</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map(member => (
              <tr
                key={member.id}
                onClick={() => handleMemberClick(member)}
                style={{ borderBottom: '1px solid var(--line)', cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-light)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <td style={{ padding: '0.875rem 0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: roleColors[member.role],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}>
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.9375rem' }}>{member.name}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{member.email}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '0.875rem 0.75rem' }}>
                  <span style={{
                    ...sectionStyles.badge,
                    background: `${roleColors[member.role]}20`,
                    color: roleColors[member.role],
                  }}>
                    {roleLabels[member.role]}
                  </span>
                </td>
                <td style={{ padding: '0.875rem 0.75rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  {member.department || '-'}
                </td>
                <td style={{ padding: '0.875rem 0.75rem', textAlign: 'center', fontSize: '0.875rem' }}>
                  {member.tasksCount}
                </td>
                <td style={{ padding: '0.875rem 0.75rem', textAlign: 'center', fontSize: '0.875rem' }}>
                  {member.hoursLogged}h
                </td>
                <td style={{ padding: '0.875rem 0.75rem' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMemberClick(member); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                  >
                    <MoreVertical size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Member Form Widget */}
      <MemberWidget
        isOpen={showForm}
        memberId={editingMember?.id}
        initialData={editingMember ? {
          name: editingMember.name,
          email: editingMember.email,
          role: editingMember.role,
          department: editingMember.department,
        } : undefined}
        onClose={() => { setShowForm(false); setEditingMember(null); }}
        onSave={handleSaveMember}
        onDelete={editingMember ? handleDeleteMember : undefined}
      />
    </div>
  );
}

// =============================================================================
// 3. MESSAGES SECTION
// =============================================================================

interface MessagesSectionProps {
  projectId: string;
  onNewMessage?: () => void;
}

export function MessagesSection({ projectId, onNewMessage }: MessagesSectionProps) {
  const { t } = useTranslation();
  const [newMessage, setNewMessage] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ProjectMessage | null>(null);
  const messages = MOCK_MESSAGES;

  const handleMessageClick = (message: ProjectMessage) => {
    setSelectedMessage(message);
    setShowDetail(true);
  };

  const handleReply = (content: string) => {
    console.log('Reply:', content, 'to message:', selectedMessage?.id);
  };

  const handlePinMessage = () => {
    console.log('Pin/unpin message:', selectedMessage?.id);
  };

  const handleDeleteMessage = () => {
    console.log('Delete message:', selectedMessage?.id);
    setShowDetail(false);
    setSelectedMessage(null);
  };

  const handleSendNewMessage = () => {
    if (newMessage.trim()) {
      console.log('Send new message:', newMessage);
      setNewMessage('');
    }
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <MessageSquare size={18} />
          {t('projects.sections.messages')}
        </div>
      </div>

      {/* New Message Input */}
      <div style={{ padding: '1rem', borderBottom: '1px solid var(--line)' }}>
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'flex-start',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'var(--brand)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 600,
          }}>
            {t('projects.you')}
          </div>
          <div style={{ flex: 1 }}>
            <textarea
              placeholder={t('projects.writeMessageToTeam')}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--line)',
                borderRadius: '0.5rem',
                resize: 'vertical',
                minHeight: '80px',
                fontSize: '0.9375rem',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary, padding: '0.375rem 0.5rem' }}>
                  <Paperclip size={16} />
                </button>
                <button style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary, padding: '0.375rem 0.5rem' }}>
                  <Image size={16} />
                </button>
              </div>
              <button
                onClick={handleSendNewMessage}
                style={{ ...sectionStyles.button, ...sectionStyles.buttonPrimary }}
                disabled={!newMessage.trim()}
              >
                <Send size={16} /> Send
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages List */}
      <div style={{ padding: '1rem' }}>
        {messages.map(message => (
          <div
            key={message.id}
            onClick={() => handleMessageClick(message)}
            style={{
              ...sectionStyles.card,
              border: message.isPinned ? '1px solid var(--brand)' : '1px solid var(--line)',
              background: message.isPinned ? 'var(--brand-light, #eff6ff)' : 'var(--bg-base)',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { if (!message.isPinned) e.currentTarget.style.background = 'var(--bg-light)'; }}
            onMouseLeave={e => { if (!message.isPinned) e.currentTarget.style.background = 'var(--bg-base)'; }}
          >
            {message.isPinned && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                color: 'var(--brand)',
                fontSize: '0.75rem',
                fontWeight: 500,
                marginBottom: '0.5rem',
              }}>
                <Star size={12} fill="currentColor" /> Pinned message
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                }}>
                  {message.author.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '0.9375rem' }}>{message.author.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                    {new Date(message.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleMessageClick(message); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <MoreHorizontal size={16} />
              </button>
            </div>
            <div style={{ marginTop: '0.75rem', fontSize: '0.9375rem', lineHeight: 1.5 }}>
              {message.content}
            </div>
            {message.attachments && message.attachments.length > 0 && (
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {message.attachments.map((file, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: 'var(--bg-light)',
                    borderRadius: '0.375rem',
                    fontSize: '0.8125rem',
                  }}>
                    <FileText size={14} />
                    <span>{file.name}</span>
                    <span style={{ color: 'var(--text-muted)' }}>({file.size})</span>
                    <Download size={14} style={{ cursor: 'pointer', color: 'var(--brand)' }} />
                  </div>
                ))}
              </div>
            )}
            {message.replies && message.replies.length > 0 && (
              <div style={{ marginTop: '1rem', paddingLeft: '1rem', borderLeft: '2px solid var(--line)' }}>
                {message.replies.map(reply => (
                  <div key={reply.id} style={{ marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>{reply.author.name}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        {new Date(reply.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>{reply.content}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem' }}>
              <button
                onClick={(e) => { e.stopPropagation(); handleMessageClick(message); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <MessageSquare size={14} /> Reply {message.replies?.length ? `(${message.replies.length})` : ''}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Message Detail Panel */}
      <MessageDetail
        isOpen={showDetail}
        message={selectedMessage ? {
          id: selectedMessage.id,
          author: selectedMessage.author,
          content: selectedMessage.content,
          createdAt: selectedMessage.createdAt,
          replies: selectedMessage.replies?.map(r => ({
            id: r.id,
            author: r.author,
            content: r.content,
            createdAt: r.createdAt,
          })),
          attachments: selectedMessage.attachments,
          isPinned: selectedMessage.isPinned,
        } : null}
        onClose={() => { setShowDetail(false); setSelectedMessage(null); }}
        onReply={handleReply}
        onPin={handlePinMessage}
        onDelete={handleDeleteMessage}
      />
    </div>
  );
}

// =============================================================================
// 4. NOTEBOOKS SECTION
// =============================================================================

interface NotebooksSectionProps {
  projectId: string;
  onNewNotebook?: () => void;
}

export function NotebooksSection({ projectId, onNewNotebook }: NotebooksSectionProps) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingNotebook, setEditingNotebook] = useState<ProjectNotebook | null>(null);
  const notebooks = MOCK_NOTEBOOKS;

  const categories = [...new Set(notebooks.map(n => n.category).filter(Boolean))];
  const filteredNotebooks = selectedCategory
    ? notebooks.filter(n => n.category === selectedCategory)
    : notebooks;

  const handleNotebookClick = (notebook: ProjectNotebook) => {
    setEditingNotebook(notebook);
    setShowForm(true);
  };

  const handleAddNotebook = () => {
    setEditingNotebook(null);
    setShowForm(true);
  };

  const handleSaveNotebook = (data: NotebookFormData) => {
    console.log('Save notebook:', data);
    setShowForm(false);
    setEditingNotebook(null);
  };

  const handleDeleteNotebook = (id: string) => {
    console.log('Delete notebook:', id);
    setShowForm(false);
    setEditingNotebook(null);
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <FileText size={18} />
          {t('projects.notebooks.title')}
        </div>
        <button
          onClick={handleAddNotebook}
          style={{ ...sectionStyles.button, ...sectionStyles.buttonPrimary }}
        >
          <Plus size={16} /> {t('projects.notebooks.new')}
        </button>
      </div>

      {/* Categories Filter */}
      <div style={{ padding: '1rem', borderBottom: '1px solid var(--line)', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => setSelectedCategory(null)}
          style={{
            ...sectionStyles.badge,
            background: !selectedCategory ? 'var(--brand)' : 'var(--bg-light)',
            color: !selectedCategory ? 'white' : 'var(--text-secondary)',
            cursor: 'pointer',
            border: 'none',
          }}
        >
          {t('common.all')}
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat!)}
            style={{
              ...sectionStyles.badge,
              background: selectedCategory === cat ? 'var(--brand)' : 'var(--bg-light)',
              color: selectedCategory === cat ? 'white' : 'var(--text-secondary)',
              cursor: 'pointer',
              border: 'none',
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Notebooks Grid */}
      <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
        {filteredNotebooks.map(notebook => (
          <div
            key={notebook.id}
            onClick={() => handleNotebookClick(notebook)}
            style={{
              ...sectionStyles.card,
              cursor: 'pointer',
              marginBottom: 0,
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--brand)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--line)')}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FileText size={20} style={{ color: 'var(--brand)' }} />
                <h4 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{notebook.title}</h4>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleNotebookClick(notebook); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <MoreVertical size={16} />
              </button>
            </div>
            {notebook.category && (
              <span style={{
                ...sectionStyles.badge,
                background: 'var(--bg-light)',
                color: 'var(--text-secondary)',
                marginTop: '0.5rem',
              }}>
                {notebook.category}
              </span>
            )}
            <div style={{ marginTop: '0.75rem', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              <div>{t('projects.notebooks.modifiedBy', { author: notebook.author })}</div>
              <div>{new Date(notebook.updatedAt).toLocaleDateString()}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Notebook Form Widget */}
      <NotebookWidget
        isOpen={showForm}
        notebookId={editingNotebook?.id}
        initialData={editingNotebook ? {
          title: editingNotebook.title,
          content: editingNotebook.content,
          category: editingNotebook.category,
        } : undefined}
        onClose={() => { setShowForm(false); setEditingNotebook(null); }}
        onSave={handleSaveNotebook}
        onDelete={editingNotebook ? handleDeleteNotebook : undefined}
      />
    </div>
  );
}

// =============================================================================
// 5. LINKS SECTION
// =============================================================================

interface LinksSectionProps {
  projectId: string;
  onAddLink?: () => void;
}

export function LinksSection({ projectId, onAddLink }: LinksSectionProps) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState<ProjectLink | null>(null);
  const links = MOCK_LINKS;
  const categories = [...new Set(links.map(l => l.category).filter(Boolean))];

  const handleLinkClick = (link: ProjectLink, e: React.MouseEvent) => {
    e.preventDefault();
    setEditingLink(link);
    setShowForm(true);
  };

  const handleAddLink = () => {
    setEditingLink(null);
    setShowForm(true);
  };

  const handleSaveLink = (data: LinkFormData) => {
    console.log('Save link:', data);
    setShowForm(false);
    setEditingLink(null);
  };

  const handleDeleteLink = (id: string) => {
    console.log('Delete link:', id);
    setShowForm(false);
    setEditingLink(null);
  };

  const handleOpenLink = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <LinkIcon size={18} />
          {t('projects.links.title')}
        </div>
        <button
          onClick={handleAddLink}
          style={{ ...sectionStyles.button, ...sectionStyles.buttonPrimary }}
        >
          <Plus size={16} /> {t('projects.links.add')}
        </button>
      </div>

      <div style={{ padding: '1rem' }}>
        {categories.map(category => (
          <div key={category} style={{ marginBottom: '1.5rem' }}>
            <h4 style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              marginBottom: '0.75rem',
            }}>
              {category}
            </h4>
            {links.filter(l => l.category === category).map(link => (
              <div
                key={link.id}
                onClick={(e) => handleLinkClick(link, e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.875rem 1rem',
                  background: 'var(--bg-light)',
                  borderRadius: '0.5rem',
                  marginBottom: '0.5rem',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'background 0.15s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-base)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg-light)')}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '0.5rem',
                  background: 'var(--bg-base)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <LinkIcon size={18} style={{ color: 'var(--brand)' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: '0.9375rem' }}>{link.title}</div>
                  {link.description && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{link.description}</div>
                  )}
                </div>
                <button
                  onClick={(e) => handleOpenLink(link.url, e)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    borderRadius: '0.25rem',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title={t('projects.links.open')}
                >
                  <ExternalLink size={16} />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Link Form Widget */}
      <LinkWidget
        isOpen={showForm}
        linkId={editingLink?.id}
        initialData={editingLink ? {
          title: editingLink.title,
          url: editingLink.url,
          description: editingLink.description,
          category: editingLink.category,
        } : undefined}
        onClose={() => { setShowForm(false); setEditingLink(null); }}
        onSave={handleSaveLink}
        onDelete={editingLink ? handleDeleteLink : undefined}
      />
    </div>
  );
}

// =============================================================================
// 6. TABLE VIEW SECTION
// =============================================================================

interface TableSectionProps {
  tasks: Task[];
  onTaskClick: (taskId: string) => void;
}

export function TableSection({ tasks, onTaskClick }: TableSectionProps) {
  const { t } = useTranslation();
  const statusLabels: Record<string, string> = {
    todo: t('projects.taskStatus.todo'),
    doing: t('projects.taskStatus.doing'),
    done: t('projects.taskStatus.done'),
  };

  const statusColors: Record<string, string> = {
    todo: '#6b7280',
    doing: '#3b82f6',
    done: '#22c55e',
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          {t('projects.tableView.title')}
        </div>
        <div style={sectionStyles.headerActions}>
          <button style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary }}>
            <Filter size={16} /> {t('common.filter')}
          </button>
          <button style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary }}>
            <Download size={16} /> {t('common.export')}
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
          <thead>
            <tr style={{ background: 'var(--bg-light)' }}>
              <th style={{ textAlign: 'left', padding: '0.875rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--line)' }}>{t('projects.tableView.task')}</th>
              <th style={{ textAlign: 'left', padding: '0.875rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--line)' }}>{t('projects.tableView.status')}</th>
              <th style={{ textAlign: 'left', padding: '0.875rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--line)' }}>{t('projects.tableView.assignee')}</th>
              <th style={{ textAlign: 'left', padding: '0.875rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--line)' }}>{t('projects.tableView.dueDate')}</th>
              <th style={{ textAlign: 'left', padding: '0.875rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--line)' }}>{t('projects.tableView.priority')}</th>
              <th style={{ textAlign: 'center', padding: '0.875rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--line)' }}>{t('projects.tableView.progress')}</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map(task => (
              <tr
                key={task.id}
                onClick={() => onTaskClick(task.id)}
                style={{ cursor: 'pointer', borderBottom: '1px solid var(--line)' }}
              >
                <td style={{ padding: '0.875rem 1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {task.status === 'done' ? (
                      <CheckCircle2 size={16} style={{ color: '#22c55e' }} />
                    ) : (
                      <Circle size={16} style={{ color: 'var(--text-muted)' }} />
                    )}
                    <span style={{ fontWeight: 500 }}>{task.title}</span>
                  </div>
                </td>
                <td style={{ padding: '0.875rem 1rem' }}>
                  <span style={{
                    ...sectionStyles.badge,
                    background: `${statusColors[task.status]}20`,
                    color: statusColors[task.status],
                  }}>
                    {statusLabels[task.status]}
                  </span>
                </td>
                <td style={{ padding: '0.875rem 1rem' }}>
                  {task.assignees?.[0] || '-'}
                </td>
                <td style={{ padding: '0.875rem 1rem' }}>
                  {task.dueAt ? (
                    <span style={{
                      color: new Date(task.dueAt) < new Date() && task.status !== 'done' ? 'var(--error)' : 'var(--text-secondary)',
                    }}>
                      {new Date(task.dueAt).toLocaleDateString()}
                    </span>
                  ) : '-'}
                </td>
                <td style={{ padding: '0.875rem 1rem' }}>
                  <span style={{
                    ...sectionStyles.badge,
                    background: task.priority === 'urgent' || task.priority === 'high' ? '#fee2e2' : task.priority === 'medium' ? '#fef3c7' : '#f3f4f6',
                    color: task.priority === 'urgent' || task.priority === 'high' ? '#dc2626' : task.priority === 'medium' ? '#d97706' : '#6b7280',
                  }}>
                    {t(`projects.priority.${task.priority || 'low'}`)}
                  </span>
                </td>
                <td style={{ padding: '0.875rem 1rem', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                    <div style={{ width: '60px', height: '6px', background: 'var(--bg-light)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ width: `${task.done ? 100 : 0}%`, height: '100%', background: 'var(--brand)', transition: 'width 0.3s' }} />
                    </div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{task.done ? 100 : 0}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// 7. TIMELINE/ACTIVITY SECTION
// =============================================================================

interface TimelineSectionProps {
  projectId: string;
}

export function TimelineSection({ projectId }: TimelineSectionProps) {
  const { t } = useTranslation();
  const activities = MOCK_ACTIVITY;

  const getActivityIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'task_completed': return <CheckCircle2 size={16} style={{ color: '#22c55e' }} />;
      case 'task_created': return <Plus size={16} style={{ color: '#3b82f6' }} />;
      case 'comment': return <MessageSquare size={16} style={{ color: '#8b5cf6' }} />;
      case 'file_uploaded': return <FileText size={16} style={{ color: '#f59e0b' }} />;
      case 'member_added': return <User size={16} style={{ color: '#06b6d4' }} />;
      case 'milestone_reached': return <Target size={16} style={{ color: '#ec4899' }} />;
      case 'time_logged': return <Clock size={16} style={{ color: '#6b7280' }} />;
      default: return <Circle size={16} />;
    }
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <Clock size={18} />
          {t('projects.timeline.title')}
        </div>
        <button style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary }}>
          <Filter size={16} /> {t('common.filter')}
        </button>
      </div>

      <div style={{ padding: '1rem' }}>
        {activities.map((activity, index) => (
          <div
            key={activity.id}
            style={{
              display: 'flex',
              gap: '1rem',
              paddingBottom: '1rem',
              marginBottom: '1rem',
              borderBottom: index < activities.length - 1 ? '1px solid var(--line)' : 'none',
            }}
          >
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'var(--bg-light)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              {getActivityIcon(activity.type)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.9375rem' }}>
                <span style={{ fontWeight: 500 }}>{activity.actor}</span>
                {' '}{activity.description}{' '}
                {activity.target && <span style={{ fontWeight: 500 }}>{activity.target}</span>}
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '0.25rem' }}>
                {new Date(activity.timestamp).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// 8. GANTT SECTION (Simplified)
// =============================================================================

interface GanttSectionProps {
  tasks: Task[];
  onTaskClick: (taskId: string) => void;
}

export function GanttSection({ tasks, onTaskClick }: GanttSectionProps) {
  const { t } = useTranslation();
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - 7);

  const days = Array.from({ length: 30 }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return date;
  });

  const getTaskPosition = (task: Task) => {
    if (!task.dueAt) return null;
    const taskDate = new Date(task.dueAt);
    const dayIndex = Math.floor((taskDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    return dayIndex >= 0 && dayIndex < 30 ? dayIndex : null;
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <BarChart3 size={18} />
          {t('projects.gantt.title')}
        </div>
        <div style={sectionStyles.headerActions}>
          <button style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary }}>
            {t('common.today')}
          </button>
          <button style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary }}>
            <Download size={16} /> {t('common.export')}
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        {/* Timeline Header */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--line)', minWidth: `${days.length * 40 + 250}px` }}>
          <div style={{ width: '250px', flexShrink: 0, padding: '0.75rem 1rem', fontWeight: 600, fontSize: '0.8125rem', background: 'var(--bg-light)' }}>
            Task
          </div>
          <div style={{ display: 'flex', flex: 1 }}>
            {days.map((day, i) => (
              <div
                key={i}
                style={{
                  width: '40px',
                  flexShrink: 0,
                  padding: '0.5rem 0',
                  textAlign: 'center',
                  fontSize: '0.625rem',
                  color: day.toDateString() === today.toDateString() ? 'var(--brand)' : 'var(--text-muted)',
                  fontWeight: day.toDateString() === today.toDateString() ? 600 : 400,
                  background: day.getDay() === 0 || day.getDay() === 6 ? 'var(--bg-light)' : 'transparent',
                  borderLeft: '1px solid var(--line)',
                }}
              >
                <div>{day.getDate()}</div>
                <div>{[t('projects.gantt.days.sun'), t('projects.gantt.days.mon'), t('projects.gantt.days.tue'), t('projects.gantt.days.wed'), t('projects.gantt.days.thu'), t('projects.gantt.days.fri'), t('projects.gantt.days.sat')][day.getDay()]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tasks */}
        {tasks.slice(0, 10).map(task => {
          const position = getTaskPosition(task);
          return (
            <div
              key={task.id}
              style={{
                display: 'flex',
                borderBottom: '1px solid var(--line)',
                minWidth: `${days.length * 40 + 250}px`,
              }}
            >
              <div
                onClick={() => onTaskClick(task.id)}
                style={{
                  width: '250px',
                  flexShrink: 0,
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {task.status === 'done' ? (
                  <CheckCircle2 size={14} style={{ color: '#22c55e', flexShrink: 0 }} />
                ) : (
                  <Circle size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                )}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.title}</span>
              </div>
              <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
                {days.map((day, i) => (
                  <div
                    key={i}
                    style={{
                      width: '40px',
                      flexShrink: 0,
                      borderLeft: '1px solid var(--line)',
                      background: day.getDay() === 0 || day.getDay() === 6 ? 'var(--bg-light)' : 'transparent',
                      position: 'relative',
                    }}
                  >
                    {day.toDateString() === today.toDateString() && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: '50%',
                        width: '2px',
                        background: 'var(--brand)',
                        transform: 'translateX(-50%)',
                      }} />
                    )}
                  </div>
                ))}
                {position !== null && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${position * 40 + 5}px`,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: '20px',
                      width: '80px',
                      background: task.status === 'done' ? '#22c55e' : task.status === 'doing' ? '#3b82f6' : '#6b7280',
                      borderRadius: '4px',
                      opacity: 0.8,
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// 9. FINANCE SECTION
// =============================================================================

interface FinanceSectionProps {
  projectId: string;
  timeEntries?: TimeEntry[];
}

// Mock invoices data - now with direction (incoming = received, outgoing = emitted)
const MOCK_INVOICES = [
  { id: 'inv-1', number: 'FT-2024-001', client: 'Acme Corp', amount: 5000, status: 'paid' as const, direction: 'outgoing' as const, date: '2024-01-15', dueDate: '2024-02-15', tags: ['project-alpha'], category: 'Services', comments: [] as string[] },
  { id: 'inv-2', number: 'FT-2024-002', client: 'Beta Srl', amount: 3500, status: 'sent' as const, direction: 'outgoing' as const, date: '2024-01-20', dueDate: '2024-02-20', tags: [], category: '', comments: [] },
  { id: 'inv-3', number: 'FT-2024-003', client: 'Gamma SpA', amount: 4000, status: 'draft' as const, direction: 'outgoing' as const, date: '2024-01-25', dueDate: '2024-02-25', tags: [], category: '', comments: [] },
  { id: 'inv-4', number: 'RIC-2024-001', client: 'Supplier XYZ', amount: 1200, status: 'paid' as const, direction: 'incoming' as const, date: '2024-01-10', dueDate: '2024-01-25', tags: ['supplier'], category: 'Materials', comments: ['Paid by bank transfer'] },
  { id: 'inv-5', number: 'RIC-2024-002', client: 'Cloud Provider', amount: 890, status: 'pending' as const, direction: 'incoming' as const, date: '2024-01-18', dueDate: '2024-02-18', tags: [], category: 'Infrastructure', comments: [] },
];

const MOCK_EXPENSES = [
  { id: 'exp-1', description: 'Licenza software', amount: 500, category: 'Software', date: '2024-01-10' },
  { id: 'exp-2', description: 'Servizi cloud AWS', amount: 350, category: 'Infrastruttura', date: '2024-01-15' },
  { id: 'exp-3', description: 'Consulenza esterna', amount: 1200, category: 'Consulenze', date: '2024-01-18' },
];

// Default categories - can be extended by user
const DEFAULT_EXPENSE_CATEGORIES = ['Software', 'Infrastruttura', 'Consulenze', 'Hardware', 'Viaggi', 'Marketing', 'Altro'];
const DEFAULT_INVOICE_CATEGORIES = ['Servizi', 'Prodotti', 'Consulenza', 'Licenze', 'Materiali', 'Manutenzione', 'Altro'];

type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'pending';
type InvoiceDirection = 'incoming' | 'outgoing';
type FinanceView = 'overview' | 'invoices' | 'expenses' | 'budget';

interface Invoice {
  id: string;
  number: string;
  client: string;
  amount: number;
  status: InvoiceStatus;
  direction: InvoiceDirection;
  date: string;
  dueDate: string;
  tags: string[];
  category: string;
  comments: string[];
}

interface Expense {
  id: string;
  description: string;
  amount: number;
  category: string;
  date: string;
}

interface Category {
  id: string;
  name: string;
  type: 'expense' | 'invoice';
}

// Mock available invoices from Accounting module (not yet linked to this project)
const AVAILABLE_INVOICES = [
  { id: 'avail-1', number: 'FT-2024-010', client: 'Omega Corp', amount: 8500, status: 'paid' as const, direction: 'outgoing' as const, date: '2024-01-28' },
  { id: 'avail-2', number: 'RIC-2024-005', client: 'Supplier ABC', amount: 2300, status: 'pending' as const, direction: 'incoming' as const, date: '2024-01-30' },
  { id: 'avail-3', number: 'FT-2024-011', client: 'Kappa Ltd', amount: 6200, status: 'sent' as const, direction: 'outgoing' as const, date: '2024-02-01' },
];

const AVAILABLE_EXPENSES = [
  { id: 'avail-exp-1', description: 'Abbonamento Figma', amount: 150, category: 'Software', date: '2024-01-22' },
  { id: 'avail-exp-2', description: 'Dominio web', amount: 45, category: 'Infrastruttura', date: '2024-01-25' },
];

export function FinanceSection({ projectId, timeEntries = [] }: FinanceSectionProps) {
  const { t } = useTranslation();
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showInvoicePicker, setShowInvoicePicker] = useState(false);
  const [showExpensePicker, setShowExpensePicker] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [currentView, setCurrentView] = useState<FinanceView>('overview');
  const [invoiceFilter, setInvoiceFilter] = useState<'all' | 'incoming' | 'outgoing'>('all');
  const [isEditMode, setIsEditMode] = useState(false); // For outgoing invoices edit confirmation
  const [linkedInvoices, setLinkedInvoices] = useState<Invoice[]>(MOCK_INVOICES);
  const [linkedExpenses, setLinkedExpenses] = useState<Expense[]>(MOCK_EXPENSES);

  // Search states for pickers
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [expenseSearch, setExpenseSearch] = useState('');
  const [invoicePickerFilter, setInvoicePickerFilter] = useState<'all' | 'incoming' | 'outgoing'>('all');

  // Category management
  const [expenseCategories, setExpenseCategories] = useState(DEFAULT_EXPENSE_CATEGORIES);
  const [invoiceCategories, setInvoiceCategories] = useState(DEFAULT_INVOICE_CATEGORIES);
  const [categoryManagerType, setCategoryManagerType] = useState<'expense' | 'invoice'>('expense');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState<string | null>(null);

  // Invoice form state
  const [invoiceForm, setInvoiceForm] = useState({
    number: '',
    client: '',
    amount: '',
    status: 'draft' as InvoiceStatus,
    direction: 'outgoing' as InvoiceDirection,
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    description: '',
    category: '',
    tags: [] as string[],
    newTag: '',
    newComment: '',
  });

  // Expense form state
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    category: 'Software',
    date: new Date().toISOString().split('T')[0],
  });

  const totalHours = timeEntries.reduce((sum, e) => sum + (e.minutes / 60), 0);
  const hourlyRate = 75;
  const totalRevenue = totalHours * hourlyRate;
  const totalInvoiced = MOCK_INVOICES.reduce((sum, inv) => sum + inv.amount, 0);
  const totalExpenses = MOCK_EXPENSES.reduce((sum, exp) => sum + exp.amount, 0);
  const budget = 25000;

  const stats = [
    { id: 'budget', label: t('projects.finance.stats.totalBudget'), value: `€ ${budget.toLocaleString()}`, icon: Wallet, color: '#3b82f6', view: 'budget' as FinanceView },
    { id: 'spent', label: t('projects.finance.stats.spent'), value: `€ ${totalExpenses.toLocaleString()}`, icon: TrendingDown, color: '#f59e0b', view: 'expenses' as FinanceView },
    { id: 'remaining', label: t('projects.finance.stats.remaining'), value: `€ ${(budget - totalExpenses).toLocaleString()}`, icon: TrendingUp, color: '#22c55e', view: 'budget' as FinanceView },
    { id: 'invoiced', label: t('projects.finance.stats.invoiced'), value: `€ ${totalInvoiced.toLocaleString()}`, icon: Receipt, color: '#8b5cf6', view: 'invoices' as FinanceView },
  ];

  const statusColors: Record<InvoiceStatus, string> = {
    draft: '#6b7280',
    sent: '#3b82f6',
    paid: '#22c55e',
    overdue: '#dc2626',
    pending: '#f59e0b',
  };

  const statusLabels: Record<InvoiceStatus, string> = {
    draft: t('projects.finance.invoiceStatus.draft'),
    sent: t('projects.finance.invoiceStatus.sent'),
    paid: t('projects.finance.invoiceStatus.paid'),
    overdue: t('projects.finance.invoiceStatus.overdue'),
    pending: t('projects.finance.invoiceStatus.pending'),
  };

  const directionLabels: Record<InvoiceDirection, string> = {
    incoming: t('projects.finance.invoiceDirection.incoming'),
    outgoing: t('projects.finance.invoiceDirection.outgoing'),
  };

  // Filter invoices by direction
  const filteredInvoices = MOCK_INVOICES.filter(inv =>
    invoiceFilter === 'all' || inv.direction === invoiceFilter
  );

  const handleStatClick = (view: FinanceView) => {
    setCurrentView(view);
  };

  // Open invoice picker to link an existing invoice
  const handleLinkInvoice = () => {
    setShowInvoicePicker(true);
  };

  // Link a selected invoice to this project
  const handleSelectInvoice = (invoice: typeof AVAILABLE_INVOICES[0]) => {
    const newInvoice: Invoice = {
      ...invoice,
      dueDate: invoice.date,
      tags: [],
      category: '',
      comments: [],
    };
    setLinkedInvoices([...linkedInvoices, newInvoice]);
    setShowInvoicePicker(false);
    showToast(t('projects.finance.invoiceLinked', { number: invoice.number }));
  };

  // Unlink invoice from project
  const handleUnlinkInvoice = (invoiceId: string) => {
    if (confirm(t('projects.finance.unlinkInvoiceConfirm'))) {
      setLinkedInvoices(linkedInvoices.filter(inv => inv.id !== invoiceId));
      setShowInvoiceForm(false);
      showToast(t('projects.finance.invoiceUnlinked'));
    }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setIsEditMode(false); // Start in view mode
    setInvoiceForm({
      number: invoice.number,
      client: invoice.client,
      amount: String(invoice.amount),
      status: invoice.status,
      direction: invoice.direction,
      date: invoice.date,
      dueDate: invoice.dueDate,
      description: '',
      category: invoice.category,
      tags: [...invoice.tags],
      newTag: '',
      newComment: '',
    });
    setShowInvoiceForm(true);
  };

  const handleEnableEditMode = () => {
    if (editingInvoice?.direction === 'outgoing') {
      const confirmed = confirm(t('projects.finance.editWarning'));
      if (confirmed) {
        setIsEditMode(true);
        showToast(t('projects.finance.editModeEnabled'));
      }
    }
  };

  const handleSaveInvoice = () => {
    if (!invoiceForm.client || !invoiceForm.amount) {
      showToast(t('projects.finance.fillRequiredFields'));
      return;
    }
    showToast(editingInvoice ? t('projects.finance.invoiceUpdated') : t('projects.finance.invoiceCreated'));
    setShowInvoiceForm(false);
    setEditingInvoice(null);
    setIsEditMode(false);
  };

  const handleSaveMetadata = () => {
    // Save only tags, category, comments (allowed for incoming invoices)
    showToast(t('projects.finance.metadataSaved'));
    setShowInvoiceForm(false);
    setEditingInvoice(null);
  };

  const handleAddTag = () => {
    if (invoiceForm.newTag.trim() && !invoiceForm.tags.includes(invoiceForm.newTag.trim())) {
      setInvoiceForm({
        ...invoiceForm,
        tags: [...invoiceForm.tags, invoiceForm.newTag.trim()],
        newTag: '',
      });
    }
  };

  const handleRemoveTag = (tag: string) => {
    setInvoiceForm({
      ...invoiceForm,
      tags: invoiceForm.tags.filter(t => t !== tag),
    });
  };

  const handleDeleteInvoice = () => {
    if (editingInvoice?.direction === 'incoming') {
      showToast(t('projects.finance.cannotDeleteReceived'));
      return;
    }
    if (confirm(t('projects.finance.deleteInvoiceConfirm'))) {
      showToast(t('projects.finance.invoiceDeleted'));
      setShowInvoiceForm(false);
      setEditingInvoice(null);
    }
  };

  // Open expense picker to link an existing expense
  const handleLinkExpense = () => {
    setShowExpensePicker(true);
  };

  // Link a selected expense to this project
  const handleSelectExpense = (expense: typeof AVAILABLE_EXPENSES[0]) => {
    setLinkedExpenses([...linkedExpenses, expense]);
    setShowExpensePicker(false);
    showToast(t('projects.finance.expenseLinked', { description: expense.description }));
  };

  // Unlink expense from project
  const handleUnlinkExpense = (expenseId: string) => {
    if (confirm(t('projects.finance.unlinkExpenseConfirm'))) {
      setLinkedExpenses(linkedExpenses.filter(exp => exp.id !== expenseId));
      setShowExpenseForm(false);
      showToast(t('projects.finance.expenseUnlinked'));
    }
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      description: expense.description,
      amount: String(expense.amount),
      category: expense.category,
      date: expense.date,
    });
    setShowExpenseForm(true);
  };

  // Category management handlers
  const handleOpenCategoryManager = (type: 'expense' | 'invoice') => {
    setCategoryManagerType(type);
    setNewCategoryName('');
    setEditingCategory(null);
    setShowCategoryManager(true);
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    const categories = categoryManagerType === 'expense' ? expenseCategories : invoiceCategories;
    if (categories.includes(newCategoryName.trim())) {
      showToast(t('projects.finance.categoryExists'));
      return;
    }
    if (categoryManagerType === 'expense') {
      setExpenseCategories([...expenseCategories, newCategoryName.trim()]);
    } else {
      setInvoiceCategories([...invoiceCategories, newCategoryName.trim()]);
    }
    setNewCategoryName('');
    showToast(t('projects.finance.categoryAdded'));
  };

  const handleUpdateCategory = (oldName: string, newName: string) => {
    if (!newName.trim() || oldName === newName) {
      setEditingCategory(null);
      return;
    }
    if (categoryManagerType === 'expense') {
      setExpenseCategories(expenseCategories.map(c => c === oldName ? newName.trim() : c));
    } else {
      setInvoiceCategories(invoiceCategories.map(c => c === oldName ? newName.trim() : c));
    }
    setEditingCategory(null);
    showToast(t('projects.finance.categoryUpdated'));
  };

  const handleDeleteCategory = (name: string) => {
    if (confirm(t('projects.finance.deleteCategoryConfirm', { name }))) {
      if (categoryManagerType === 'expense') {
        setExpenseCategories(expenseCategories.filter(c => c !== name));
      } else {
        setInvoiceCategories(invoiceCategories.filter(c => c !== name));
      }
      showToast(t('projects.finance.categoryDeleted'));
    }
  };

  const handleSaveExpense = () => {
    if (!expenseForm.description || !expenseForm.amount) {
      showToast(t('projects.finance.fillRequiredFields'));
      return;
    }
    showToast(editingExpense ? t('projects.finance.expenseUpdated') : t('projects.finance.expenseRegistered'));
    setShowExpenseForm(false);
    setEditingExpense(null);
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <DollarSign size={18} />
          {t('projects.finance.title')}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleLinkExpense}
            style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary }}
          >
            <LinkIcon size={16} /> {t('projects.finance.linkExpense')}
          </button>
          <button
            onClick={handleLinkInvoice}
            style={{ ...sectionStyles.button, ...sectionStyles.buttonPrimary }}
          >
            <LinkIcon size={16} /> {t('projects.finance.linkInvoice')}
          </button>
        </div>
      </div>

      {/* View Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', padding: '0.5rem 1rem', borderBottom: '1px solid var(--line)' }}>
        {[
          { id: 'overview' as FinanceView, label: t('projects.finance.tabs.overview') },
          { id: 'invoices' as FinanceView, label: t('projects.finance.tabs.invoices') },
          { id: 'expenses' as FinanceView, label: t('projects.finance.tabs.expenses') },
          { id: 'budget' as FinanceView, label: t('projects.finance.tabs.budget') },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setCurrentView(tab.id)}
            style={{
              padding: '0.5rem 1rem',
              background: currentView === tab.id ? 'var(--brand)' : 'transparent',
              color: currentView === tab.id ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              transition: 'all 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Stats Grid - always visible */}
      <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
        {stats.map((stat) => (
          <div
            key={stat.id}
            onClick={() => handleStatClick(stat.view)}
            style={{
              padding: '1.25rem',
              background: currentView === stat.view ? 'var(--brand-light, #eff6ff)' : 'var(--bg-light)',
              borderRadius: '0.75rem',
              border: `1px solid ${currentView === stat.view ? 'var(--brand)' : 'var(--line)'}`,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <stat.icon size={16} style={{ color: stat.color }} />
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{stat.label}</span>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Invoices View */}
      {(currentView === 'overview' || currentView === 'invoices') && (
        <div style={{ padding: '0 1rem 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b7280' }}>{t('projects.finance.tabs.invoices')}</h3>
              {currentView === 'invoices' && (
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  {(['all', 'outgoing', 'incoming'] as const).map(filter => (
                    <button
                      key={filter}
                      onClick={() => setInvoiceFilter(filter)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        background: invoiceFilter === filter ? '#3b82f6' : '#f3f4f6',
                        color: invoiceFilter === filter ? 'white' : '#6b7280',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: 'pointer',
                      }}
                    >
                      {t(`projects.finance.filters.${filter}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {currentView === 'overview' && (
              <button
                onClick={() => setCurrentView('invoices')}
                style={{ ...sectionStyles.button, fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {t('common.viewAll')}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {(currentView === 'overview' ? linkedInvoices.slice(0, 4) : linkedInvoices.filter(inv => invoiceFilter === 'all' || inv.direction === invoiceFilter)).map(invoice => (
              <div
                key={invoice.id}
                onClick={() => handleEditInvoice(invoice)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem 1rem',
                  background: '#f9fafb',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  borderLeft: `3px solid ${invoice.direction === 'incoming' ? '#22c55e' : '#3b82f6'}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: invoice.direction === 'incoming' ? '#dcfce7' : '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {invoice.direction === 'incoming' ? (
                      <ArrowRight size={16} style={{ color: '#22c55e', transform: 'rotate(180deg)' }} />
                    ) : (
                      <ArrowRight size={16} style={{ color: '#3b82f6' }} />
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{invoice.number}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {invoice.client} • {directionLabels[invoice.direction]}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{
                    fontWeight: 600,
                    color: invoice.direction === 'incoming' ? '#dc2626' : '#22c55e',
                  }}>
                    {invoice.direction === 'incoming' ? '-' : '+'}€ {invoice.amount.toLocaleString(undefined)}
                  </span>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    background: `${statusColors[invoice.status]}20`,
                    color: statusColors[invoice.status],
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  }}>
                    {statusLabels[invoice.status]}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expenses View */}
      {(currentView === 'overview' || currentView === 'expenses') && (
        <div style={{ padding: '0 1rem 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{t('projects.finance.recentExpenses')}</h3>
            {currentView === 'overview' && (
              <button
                onClick={() => setCurrentView('expenses')}
                style={{ ...sectionStyles.button, fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {t('common.viewAll')}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {linkedExpenses.map(expense => (
              <div
                key={expense.id}
                onClick={() => handleEditExpense(expense)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem 1rem',
                  background: 'var(--bg-light)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <TrendingDown size={16} style={{ color: '#f59e0b' }} />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{expense.description}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{expense.category} • {expense.date}</div>
                  </div>
                </div>
                <span style={{ fontWeight: 600, color: '#f59e0b' }}>-€ {expense.amount.toLocaleString(undefined)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Budget View */}
      {currentView === 'budget' && (
        <div style={{ padding: '0 1rem 1rem' }}>
          <div style={{ padding: '1rem', background: 'var(--bg-light)', borderRadius: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t('projects.finance.budgetUsage')}</span>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{Math.round((totalExpenses / budget) * 100)}%</span>
            </div>
            <div style={{ height: '12px', background: 'var(--bg-base)', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min((totalExpenses / budget) * 100, 100)}%`,
                height: '100%',
                background: totalExpenses / budget > 0.8 ? '#f59e0b' : '#22c55e',
                transition: 'width 0.3s',
              }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ padding: '1rem', background: 'var(--bg-light)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{t('projects.finance.budgetAllocated')}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>€ {budget.toLocaleString()}</div>
            </div>
            <div style={{ padding: '1rem', background: 'var(--bg-light)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{t('projects.finance.spentSoFar')}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#f59e0b' }}>€ {totalExpenses.toLocaleString()}</div>
            </div>
            <div style={{ padding: '1rem', background: 'var(--bg-light)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{t('projects.finance.stats.remaining')}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#22c55e' }}>€ {(budget - totalExpenses).toLocaleString()}</div>
            </div>
            <div style={{ padding: '1rem', background: 'var(--bg-light)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{t('projects.finance.stats.invoiced')}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#8b5cf6' }}>€ {totalInvoiced.toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Form Modal */}
      {showInvoiceForm && (() => {
        const isIncoming = editingInvoice?.direction === 'incoming';
        const isOutgoing = editingInvoice?.direction === 'outgoing';
        const canEdit = !editingInvoice || (isOutgoing && isEditMode) || invoiceForm.status === 'draft';
        const isReadOnly: boolean = !!(isIncoming || (isOutgoing && !isEditMode && editingInvoice));

        return (
          <div style={formStyles.overlay} onClick={() => { setShowInvoiceForm(false); setIsEditMode(false); }}>
            <div style={formStyles.panel} onClick={(e) => e.stopPropagation()}>
              <div style={formStyles.header}>
                <div style={formStyles.headerTitle}>
                  <Receipt size={20} />
                  {!editingInvoice ? t('projects.finance.newInvoice') :
                    isIncoming ? t('projects.finance.receivedInvoiceReadOnly') :
                    isEditMode ? t('projects.finance.editInvoiceWarning') : t('projects.finance.invoiceDetail')}
                </div>
                <button onClick={() => { setShowInvoiceForm(false); setIsEditMode(false); }} style={formStyles.buttonIcon}>
                  <X size={20} />
                </button>
              </div>

              {/* Warning banner for incoming invoices */}
              {isIncoming && (
                <div style={{
                  padding: '0.75rem 1rem',
                  background: '#fef3c7',
                  borderBottom: '1px solid #fcd34d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  color: '#92400e',
                }}>
                  <AlertTriangle size={16} />
                  {t('projects.finance.receivedInvoiceWarning')}
                </div>
              )}

              {/* Warning banner for outgoing invoices in edit mode */}
              {isOutgoing && isEditMode && (
                <div style={{
                  padding: '0.75rem 1rem',
                  background: '#fef2f2',
                  borderBottom: '1px solid #fecaca',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  color: '#dc2626',
                }}>
                  <AlertTriangle size={16} />
                  {t('projects.finance.editingIssuedWarning')}
                </div>
              )}

              <div style={formStyles.content}>
                {/* Core invoice fields - read-only for incoming, editable for new/outgoing in edit mode */}
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('projects.finance.invoiceNumber')} *</label>
                  <input
                    type="text"
                    value={invoiceForm.number}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, number: e.target.value })}
                    style={{ ...formStyles.input, ...(isReadOnly ? { background: '#f3f4f6', cursor: 'not-allowed' } : {}) }}
                    disabled={isReadOnly}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{isIncoming ? t('projects.finance.supplier') : t('projects.finance.client')} *</label>
                  <input
                    type="text"
                    value={invoiceForm.client}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, client: e.target.value })}
                    style={{ ...formStyles.input, ...(isReadOnly ? { background: '#f3f4f6', cursor: 'not-allowed' } : {}) }}
                    disabled={isReadOnly}
                  />
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('common.amount')} (€) *</label>
                  <input
                    type="number"
                    value={invoiceForm.amount}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                    style={{ ...formStyles.input, ...(isReadOnly ? { background: '#f3f4f6', cursor: 'not-allowed' } : {}) }}
                    disabled={isReadOnly}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div style={formStyles.formGroup}>
                    <label style={formStyles.label}>{t('common.date')}</label>
                    <input
                      type="date"
                      value={invoiceForm.date}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, date: e.target.value })}
                      style={{ ...formStyles.input, ...(isReadOnly ? { background: '#f3f4f6', cursor: 'not-allowed' } : {}) }}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div style={formStyles.formGroup}>
                    <label style={formStyles.label}>{t('projects.finance.dueDate')}</label>
                    <input
                      type="date"
                      value={invoiceForm.dueDate}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })}
                      style={{ ...formStyles.input, ...(isReadOnly ? { background: '#f3f4f6', cursor: 'not-allowed' } : {}) }}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('common.status')}</label>
                  <select
                    value={invoiceForm.status}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, status: e.target.value as InvoiceStatus })}
                    style={{ ...formStyles.select, ...(isReadOnly ? { background: '#f3f4f6', cursor: 'not-allowed' } : {}) }}
                    disabled={isReadOnly}
                  >
                    <option value="draft">{t('projects.finance.invoiceStatus.draft')}</option>
                    <option value="sent">{t('projects.finance.invoiceStatus.sent')}</option>
                    <option value="pending">{t('projects.finance.invoiceStatus.pending')}</option>
                    <option value="paid">{t('projects.finance.invoiceStatus.paid')}</option>
                    <option value="overdue">{t('projects.finance.invoiceStatus.overdue')}</option>
                  </select>
                </div>

                {/* Divider */}
                <div style={{ borderTop: '1px solid #e5e7eb', margin: '1.5rem 0', paddingTop: '1rem' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>
                    {t('projects.finance.metadataAlwaysEditable')}
                  </div>
                </div>

                {/* Category - always editable */}
                <div style={formStyles.formGroup}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label style={{ ...formStyles.label, marginBottom: 0 }}>{t('common.category')}</label>
                    <button
                      onClick={() => handleOpenCategoryManager('invoice')}
                      style={{ fontSize: '0.75rem', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                    >
                      <Plus size={12} /> {t('projects.finance.manageCategories')}
                    </button>
                  </div>
                  <select
                    value={invoiceForm.category}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, category: e.target.value })}
                    style={formStyles.select}
                  >
                    <option value="">{t('projects.finance.selectCategory')}</option>
                    {invoiceCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Tags - always editable */}
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('common.tags')}</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    {invoiceForm.tags.map(tag => (
                      <span
                        key={tag}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          background: '#dbeafe',
                          color: '#1e40af',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                        }}
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#1e40af' }}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={invoiceForm.newTag}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, newTag: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      style={{ ...formStyles.input, flex: 1 }}
                      placeholder={t('projects.finance.addTagPlaceholder')}
                    />
                    <button onClick={handleAddTag} style={formStyles.buttonSecondary}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* Comment - always editable */}
                <div style={formStyles.formGroup}>
                  <label style={formStyles.label}>{t('common.newComment')}</label>
                  <textarea
                    value={invoiceForm.newComment}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, newComment: e.target.value })}
                    style={{ ...formStyles.input, minHeight: '60px', resize: 'vertical' }}
                    placeholder={t('projects.finance.addCommentPlaceholder')}
                  />
                </div>
              </div>

              <div style={formStyles.footer}>
                {/* Unlink button - unlink invoice from this project */}
                {editingInvoice && (
                  <button onClick={() => handleUnlinkInvoice(editingInvoice.id)} style={{ ...formStyles.buttonSecondary, color: '#dc2626', borderColor: '#fecaca' }}>
                    <LinkIcon size={16} /> {t('common.unlink')}
                  </button>
                )}

                <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                  <button onClick={() => { setShowInvoiceForm(false); setIsEditMode(false); }} style={formStyles.buttonSecondary}>
                    {t('common.close')}
                  </button>

                  {/* Save metadata button - always available for tags/category/comments */}
                  {editingInvoice && (
                    <button onClick={handleSaveMetadata} style={formStyles.buttonPrimary}>
                      {t('projects.finance.saveMetadata')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Expense Detail Modal - Read only since expenses are linked, not created */}
      {showExpenseForm && (
        <div style={formStyles.overlay} onClick={() => setShowExpenseForm(false)}>
          <div style={formStyles.panel} onClick={(e) => e.stopPropagation()}>
            <div style={formStyles.header}>
              <div style={formStyles.headerTitle}>
                <TrendingDown size={20} />
                {t('projects.finance.expenseDetail')}
              </div>
              <button onClick={() => setShowExpenseForm(false)} style={formStyles.buttonIcon}>
                <X size={20} />
              </button>
            </div>

            {/* Info banner */}
            <div style={{
              padding: '0.75rem 1rem',
              background: '#f0f9ff',
              borderBottom: '1px solid #bae6fd',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: '#0369a1',
            }}>
              <LinkIcon size={16} />
              {t('projects.finance.expenseLinkedInfo')}
            </div>

            <div style={formStyles.content}>
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t('common.description')}</label>
                <input
                  type="text"
                  value={expenseForm.description}
                  style={{ ...formStyles.input, background: '#f3f4f6', cursor: 'not-allowed' }}
                  disabled
                />
              </div>
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>Importo (€)</label>
                <input
                  type="number"
                  value={expenseForm.amount}
                  style={{ ...formStyles.input, background: '#f3f4f6', cursor: 'not-allowed' }}
                  disabled
                />
              </div>
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t('common.category')}</label>
                <input
                  type="text"
                  value={expenseForm.category}
                  style={{ ...formStyles.input, background: '#f3f4f6', cursor: 'not-allowed' }}
                  disabled
                />
              </div>
              <div style={formStyles.formGroup}>
                <label style={formStyles.label}>{t('common.date')}</label>
                <input
                  type="date"
                  value={expenseForm.date}
                  style={{ ...formStyles.input, background: '#f3f4f6', cursor: 'not-allowed' }}
                  disabled
                />
              </div>
            </div>
            <div style={formStyles.footer}>
              {editingExpense && (
                <button
                  onClick={() => handleUnlinkExpense(editingExpense.id)}
                  style={{ ...formStyles.buttonSecondary, color: '#dc2626', borderColor: '#fecaca' }}
                >
                  <LinkIcon size={16} /> {t('common.unlink')}
                </button>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                <button onClick={() => setShowExpenseForm(false)} style={formStyles.buttonSecondary}>
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <div style={formStyles.overlay} onClick={() => setShowCategoryManager(false)}>
          <div style={{ ...formStyles.panel, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div style={formStyles.header}>
              <div style={formStyles.headerTitle}>
                <Tag size={20} />
                {t('projects.finance.categoryManager')} {categoryManagerType === 'expense' ? t('projects.finance.expenseCategories') : t('projects.finance.invoiceCategories')}
              </div>
              <button onClick={() => setShowCategoryManager(false)} style={formStyles.buttonIcon}>
                <X size={20} />
              </button>
            </div>
            <div style={formStyles.content}>
              {/* Add new category */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                  style={{ ...formStyles.input, flex: 1 }}
                  placeholder={t('projects.finance.newCategoryPlaceholder')}
                />
                <button onClick={handleAddCategory} style={formStyles.buttonPrimary}>
                  <Plus size={16} /> {t('common.add')}
                </button>
              </div>

              {/* Category list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {(categoryManagerType === 'expense' ? expenseCategories : invoiceCategories).map(cat => (
                  <div
                    key={cat}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem 1rem',
                      background: '#f9fafb',
                      borderRadius: '0.5rem',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    {editingCategory === cat ? (
                      <input
                        type="text"
                        defaultValue={cat}
                        autoFocus
                        onBlur={(e) => handleUpdateCategory(cat, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdateCategory(cat, (e.target as HTMLInputElement).value);
                          if (e.key === 'Escape') setEditingCategory(null);
                        }}
                        style={{ ...formStyles.input, padding: '0.25rem 0.5rem', flex: 1, marginRight: '0.5rem' }}
                      />
                    ) : (
                      <span style={{ fontWeight: 500 }}>{cat}</span>
                    )}
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        onClick={() => setEditingCategory(cat)}
                        style={{ ...formStyles.buttonIcon, padding: '0.25rem' }}
                        title={t('common.edit')}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat)}
                        style={{ ...formStyles.buttonIcon, padding: '0.25rem', color: '#dc2626' }}
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {(categoryManagerType === 'expense' ? expenseCategories : invoiceCategories).length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  {t('projects.finance.noCategories')}
                </div>
              )}
            </div>
            <div style={formStyles.footer}>
              <button onClick={() => setShowCategoryManager(false)} style={formStyles.buttonPrimary}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Picker Modal */}
      {showInvoicePicker && (() => {
        const searchLower = invoiceSearch.toLowerCase();
        const filteredAvailableInvoices = AVAILABLE_INVOICES.filter(inv => {
          // Not already linked
          if (linkedInvoices.find(li => li.id === inv.id)) return false;
          // Direction filter
          if (invoicePickerFilter !== 'all' && inv.direction !== invoicePickerFilter) return false;
          // Search filter
          if (searchLower && !inv.number.toLowerCase().includes(searchLower) &&
              !inv.client.toLowerCase().includes(searchLower)) return false;
          return true;
        });

        return (
          <div style={formStyles.overlay} onClick={() => { setShowInvoicePicker(false); setInvoiceSearch(''); }}>
            <div style={{ ...formStyles.panel, maxWidth: '550px' }} onClick={(e) => e.stopPropagation()}>
              <div style={formStyles.header}>
                <div style={formStyles.headerTitle}>
                  <LinkIcon size={20} />
                  {t('projects.finance.linkInvoiceTitle')}
                </div>
                <button onClick={() => { setShowInvoicePicker(false); setInvoiceSearch(''); }} style={formStyles.buttonIcon}>
                  <X size={20} />
                </button>
              </div>
              <div style={formStyles.content}>
                {/* Search input */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input
                      type="text"
                      value={invoiceSearch}
                      onChange={(e) => setInvoiceSearch(e.target.value)}
                      style={{
                        ...formStyles.input,
                        paddingLeft: '2.25rem',
                      }}
                      placeholder={t('projects.finance.searchByNumberOrClient')}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Filter tabs */}
                <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem' }}>
                  {([
                    { id: 'all' as const, label: t('projects.finance.filters.all') },
                    { id: 'outgoing' as const, label: t('projects.finance.filters.outgoing') },
                    { id: 'incoming' as const, label: t('projects.finance.filters.incoming') },
                  ]).map(filter => (
                    <button
                      key={filter.id}
                      onClick={() => setInvoicePickerFilter(filter.id)}
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.8125rem',
                        background: invoicePickerFilter === filter.id ? '#3b82f6' : '#f3f4f6',
                        color: invoicePickerFilter === filter.id ? 'white' : '#6b7280',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>

                {/* Results */}
                {filteredAvailableInvoices.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    {invoiceSearch ? t('projects.finance.noInvoicesFound') : t('projects.finance.noInvoicesAvailable')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                    {filteredAvailableInvoices.map(invoice => (
                      <div
                        key={invoice.id}
                        onClick={() => { handleSelectInvoice(invoice); setInvoiceSearch(''); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem 1rem',
                          background: '#f9fafb',
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          transition: 'background 0.15s',
                          borderLeft: `3px solid ${invoice.direction === 'incoming' ? '#22c55e' : '#3b82f6'}`,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: invoice.direction === 'incoming' ? '#dcfce7' : '#dbeafe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            {invoice.direction === 'incoming' ? (
                              <ArrowRight size={16} style={{ color: '#22c55e', transform: 'rotate(180deg)' }} />
                            ) : (
                              <ArrowRight size={16} style={{ color: '#3b82f6' }} />
                            )}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{invoice.number}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {invoice.client} • {invoice.date}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{
                            fontWeight: 600,
                            color: invoice.direction === 'incoming' ? '#dc2626' : '#22c55e',
                          }}>
                            {invoice.direction === 'incoming' ? '-' : '+'}€ {invoice.amount.toLocaleString(undefined)}
                          </span>
                          <LinkIcon size={16} style={{ color: '#3b82f6' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={formStyles.footer}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {t('projects.finance.invoicesAvailable', { count: filteredAvailableInvoices.length })}
                </span>
                <button onClick={() => { setShowInvoicePicker(false); setInvoiceSearch(''); }} style={formStyles.buttonSecondary}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Expense Picker Modal */}
      {showExpensePicker && (() => {
        const searchLower = expenseSearch.toLowerCase();
        const filteredAvailableExpenses = AVAILABLE_EXPENSES.filter(exp => {
          // Not already linked
          if (linkedExpenses.find(le => le.id === exp.id)) return false;
          // Search filter
          if (searchLower && !exp.description.toLowerCase().includes(searchLower) &&
              !exp.category.toLowerCase().includes(searchLower)) return false;
          return true;
        });

        return (
          <div style={formStyles.overlay} onClick={() => { setShowExpensePicker(false); setExpenseSearch(''); }}>
            <div style={{ ...formStyles.panel, maxWidth: '550px' }} onClick={(e) => e.stopPropagation()}>
              <div style={formStyles.header}>
                <div style={formStyles.headerTitle}>
                  <LinkIcon size={20} />
                  {t('projects.finance.linkExpenseTitle')}
                </div>
                <button onClick={() => { setShowExpensePicker(false); setExpenseSearch(''); }} style={formStyles.buttonIcon}>
                  <X size={20} />
                </button>
              </div>
              <div style={formStyles.content}>
                {/* Search input */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input
                      type="text"
                      value={expenseSearch}
                      onChange={(e) => setExpenseSearch(e.target.value)}
                      style={{
                        ...formStyles.input,
                        paddingLeft: '2.25rem',
                      }}
                      placeholder={t('projects.finance.searchByDescriptionOrCategory')}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Results */}
                {filteredAvailableExpenses.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    {expenseSearch ? t('projects.finance.noExpensesFound') : t('projects.finance.noExpensesAvailable')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                    {filteredAvailableExpenses.map(expense => (
                      <div
                        key={expense.id}
                        onClick={() => { handleSelectExpense(expense); setExpenseSearch(''); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem 1rem',
                          background: '#f9fafb',
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          transition: 'background 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <TrendingDown size={16} style={{ color: '#f59e0b' }} />
                          <div>
                            <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{expense.description}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {expense.category} • {expense.date}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontWeight: 600, color: '#f59e0b' }}>
                            -€ {expense.amount.toLocaleString(undefined)}
                          </span>
                          <LinkIcon size={16} style={{ color: '#3b82f6' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={formStyles.footer}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {t('projects.finance.expensesAvailable', { count: filteredAvailableExpenses.length })}
                </span>
                <button onClick={() => { setShowExpensePicker(false); setExpenseSearch(''); }} style={formStyles.buttonSecondary}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// =============================================================================
// 10. RISKS SECTION
// =============================================================================

interface RisksSectionProps {
  projectId: string;
  onAddRisk?: () => void;
}

export function RisksSection({ projectId, onAddRisk }: RisksSectionProps) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const risks = MOCK_RISKS;

  const probabilityColors = { low: '#22c55e', medium: '#f59e0b', high: '#dc2626' };
  const impactColors = { low: '#22c55e', medium: '#f59e0b', high: '#dc2626' };
  const statusLabels = {
    identified: t('projects.risks.status.identified'),
    mitigating: t('projects.risks.status.mitigating'),
    resolved: t('projects.risks.status.resolved'),
    accepted: t('projects.risks.status.accepted')
  };

  const handleRiskClick = (risk: Risk) => {
    setEditingRisk(risk);
    setShowForm(true);
  };

  const handleAddRisk = () => {
    setEditingRisk(null);
    setShowForm(true);
  };

  const handleSaveRisk = (data: RiskFormData) => {
    console.log('Save risk:', data);
    setShowForm(false);
    setEditingRisk(null);
  };

  const handleDeleteRisk = (id: string) => {
    console.log('Delete risk:', id);
    setShowForm(false);
    setEditingRisk(null);
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <AlertTriangle size={18} />
          {t('projects.risks.title')}
        </div>
        <button
          onClick={handleAddRisk}
          style={{ ...sectionStyles.button, ...sectionStyles.buttonPrimary }}
        >
          <Plus size={16} /> {t('projects.risks.new')}
        </button>
      </div>

      <div style={{ padding: '1rem' }}>
        {risks.map(risk => (
          <div
            key={risk.id}
            onClick={() => handleRiskClick(risk)}
            style={{
              ...sectionStyles.card,
              borderLeft: `4px solid ${impactColors[risk.impact]}`,
              cursor: 'pointer',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-light)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg-base)')}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{risk.title}</h4>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0.5rem 0' }}>
                  {risk.description}
                </p>
              </div>
              <span style={{
                ...sectionStyles.badge,
                background: 'var(--bg-light)',
                color: 'var(--text-secondary)',
              }}>
                {statusLabels[risk.status]}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('projects.risks.probability')}:</span>
                <span style={{
                  ...sectionStyles.badge,
                  background: `${probabilityColors[risk.probability]}20`,
                  color: probabilityColors[risk.probability],
                }}>
                  {t(`projects.risks.level.${risk.probability}`)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('projects.risks.impact')}:</span>
                <span style={{
                  ...sectionStyles.badge,
                  background: `${impactColors[risk.impact]}20`,
                  color: impactColors[risk.impact],
                }}>
                  {t(`projects.risks.level.${risk.impact}`)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <User size={14} style={{ color: 'var(--text-muted)' }} />
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{risk.owner}</span>
              </div>
            </div>

            {risk.mitigation && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: 'var(--bg-light)',
                borderRadius: '0.375rem',
                fontSize: '0.8125rem',
              }}>
                <span style={{ fontWeight: 500 }}>Mitigazione: </span>
                {risk.mitigation}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Risk Form Widget */}
      <RiskWidget
        isOpen={showForm}
        riskId={editingRisk?.id}
        initialData={editingRisk ? {
          title: editingRisk.title,
          description: editingRisk.description,
          probability: editingRisk.probability,
          impact: editingRisk.impact,
          status: editingRisk.status,
          owner: editingRisk.owner,
          mitigation: editingRisk.mitigation,
        } : undefined}
        onClose={() => { setShowForm(false); setEditingRisk(null); }}
        onSave={handleSaveRisk}
        onDelete={editingRisk ? handleDeleteRisk : undefined}
      />
    </div>
  );
}

// =============================================================================
// 11. SETTINGS SECTION
// =============================================================================

interface SettingsSectionProps {
  project: { id: string; title: string; description?: string };
  onEditProject?: () => void;
}

export function SettingsSection({ project, onEditProject }: SettingsSectionProps) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState({
    newTasks: true,
    comments: true,
    deadlines: true,
    milestones: false,
  });

  const handleToggle = (key: keyof typeof notifications) => {
    setNotifications(prev => {
      const newValue = !prev[key];
      showToast(`${key}: ${newValue ? t('common.enabled') : t('common.disabled')}`);
      return { ...prev, [key]: newValue };
    });
  };

  const handleEditDescription = () => {
    if (onEditProject) {
      onEditProject();
    } else {
      showToast('Edit description');
    }
  };

  const handleArchive = () => {
    if (confirm(t('projects.confirmArchive'))) {
      showToast(t('projects.toasts.projectArchived'));
    }
  };

  const handleDelete = () => {
    if (confirm(t('projects.confirmDelete'))) {
      showToast(t('projects.toasts.projectDeleted'));
    }
  };

  const notificationSettings = [
    { key: 'newTasks' as const, label: t('projects.settings.newTasksAssigned') },
    { key: 'comments' as const, label: t('projects.settings.commentsOnMyTasks') },
    { key: 'deadlines' as const, label: t('projects.settings.upcomingDeadlines') },
    { key: 'milestones' as const, label: t('projects.settings.milestoneUpdates') },
  ];

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          {t('projects.settings.title')}
        </div>
      </div>

      <div style={{ padding: '1.5rem' }}>
        {/* General Settings */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>{t('projects.settings.general')}</h3>

          <div style={{ display: 'grid', gap: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: 'var(--bg-light)', borderRadius: '0.5rem' }}>
              <div>
                <div style={{ fontWeight: 500 }}>{t('projects.settings.projectName')}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{project.title}</div>
              </div>
              <button
                onClick={onEditProject}
                style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary }}
              >
                <Edit2 size={14} /> {t('common.edit')}
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', background: 'var(--bg-light)', borderRadius: '0.5rem' }}>
              <div>
                <div style={{ fontWeight: 500 }}>{t('tasks.description')}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{project.description || t('tasks.noDescription')}</div>
              </div>
              <button
                onClick={handleEditDescription}
                style={{ ...sectionStyles.button, ...sectionStyles.buttonSecondary }}
              >
                <Edit2 size={14} /> {t('common.edit')}
              </button>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>{t('settings.notifications.title')}</h3>

          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {notificationSettings.map((setting) => (
              <div
                key={setting.key}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  background: 'var(--bg-light)',
                  borderRadius: '0.5rem',
                }}
              >
                <span>{setting.label}</span>
                <div
                  onClick={() => handleToggle(setting.key)}
                  style={{
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    background: notifications[setting.key] ? 'var(--brand)' : 'var(--line)',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: 'white',
                      position: 'absolute',
                      top: '2px',
                      left: notifications[setting.key] ? '22px' : '2px',
                      transition: 'left 0.15s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Danger Zone */}
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--error)' }}>Danger zone</h3>

          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#fef2f2',
              borderRadius: '0.5rem',
              border: '1px solid #fecaca',
            }}>
              <div>
                <div style={{ fontWeight: 500 }}>Archive project</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>The project will be hidden but recoverable</div>
              </div>
              <button
                onClick={handleArchive}
                style={{
                  ...sectionStyles.button,
                  background: '#fee2e2',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                }}
              >
                <Archive size={14} /> Archivia
              </button>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#fef2f2',
              borderRadius: '0.5rem',
              border: '1px solid #fecaca',
            }}>
              <div>
                <div style={{ fontWeight: 500 }}>{t('projects.deleteProject')}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{t('projects.deleteWarning')}</div>
              </div>
              <button
                onClick={handleDelete}
                style={{
                  ...sectionStyles.button,
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                }}
              >
                <Trash2 size={14} /> {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// PLACEHOLDER SECTIONS (Tickets, Proofs, Forms)
// =============================================================================

interface TicketsSectionProps {
  projectId: string;
  onTicketClick?: (ticketId: string) => void;
  onAddTicket?: () => void;
}

export function TicketsSection({ projectId, onTicketClick, onAddTicket }: TicketsSectionProps) {
  const { t } = useTranslation();
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const tickets = MOCK_TICKETS;

  const typeColors: Record<string, string> = { bug: '#dc2626', feature: '#8b5cf6', support: '#3b82f6', question: '#22c55e' };
  const typeLabels: Record<string, string> = { bug: t('projects.tickets.bug'), feature: t('projects.tickets.feature'), support: t('projects.tickets.support'), question: t('projects.tickets.question') };
  const statusLabels: Record<string, string> = { open: t('projects.tickets.open'), 'in-progress': t('projects.tickets.inProgress'), waiting: t('projects.tickets.waiting'), resolved: t('projects.tickets.resolved'), closed: t('projects.tickets.closed') };
  const priorityLabels: Record<string, string> = { low: t('projects.priority.low'), medium: t('projects.priority.medium'), high: t('projects.priority.high'), urgent: t('projects.priority.urgent') };

  const handleTicketClick = (ticket: Ticket) => {
    // Use deep linking if handler provided
    if (onTicketClick) {
      onTicketClick(ticket.id);
    }
  };

  const handleAddTicket = () => {
    if (onAddTicket) {
      onAddTicket();
    }
  };

  const filteredTickets = filterStatus ? tickets.filter(t => t.status === filterStatus) : tickets;

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <Flag size={18} />
          Tickets
          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({tickets.length})</span>
        </div>
        <button onClick={handleAddTicket} style={{ ...sectionStyles.button, ...sectionStyles.buttonPrimary }}>
          <Plus size={16} /> New ticket
        </button>
      </div>

      {/* Status Filter */}
      <div style={{ padding: '1rem', borderBottom: '1px solid var(--line)', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => setFilterStatus(null)}
          style={{
            ...sectionStyles.badge,
            background: !filterStatus ? 'var(--brand)' : 'var(--bg-light)',
            color: !filterStatus ? 'white' : 'var(--text-secondary)',
            cursor: 'pointer',
            border: 'none',
          }}
        >
          {t('common.all')}
        </button>
        {['open', 'in-progress', 'waiting', 'resolved'].map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            style={{
              ...sectionStyles.badge,
              background: filterStatus === status ? 'var(--brand)' : 'var(--bg-light)',
              color: filterStatus === status ? 'white' : 'var(--text-secondary)',
              cursor: 'pointer',
              border: 'none',
            }}
          >
            {statusLabels[status]}
          </button>
        ))}
      </div>

      {/* Tickets List */}
      <div style={{ padding: '1rem' }}>
        {filteredTickets.length === 0 ? (
          <div style={sectionStyles.emptyState}>
            <Flag size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>{t('projects.tickets.noTickets')}</h3>
            <p style={{ maxWidth: '300px' }}>{t('projects.tickets.noTicketsDescription')}</p>
          </div>
        ) : (
          filteredTickets.map(ticket => (
            <div
              key={ticket.id}
              onClick={() => handleTicketClick(ticket)}
              style={{
                ...sectionStyles.card,
                borderLeft: `4px solid ${typeColors[ticket.type]}`,
                cursor: 'pointer',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-light)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg-base)')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{ticket.id}</span>
                    <span style={{
                      ...sectionStyles.badge,
                      background: `${typeColors[ticket.type]}20`,
                      color: typeColors[ticket.type],
                    }}>
                      {typeLabels[ticket.type]}
                    </span>
                    <span style={{
                      ...sectionStyles.badge,
                      background: ticket.priority === 'urgent' || ticket.priority === 'high' ? '#fee2e2' : ticket.priority === 'medium' ? '#fef3c7' : '#f3f4f6',
                      color: ticket.priority === 'urgent' || ticket.priority === 'high' ? '#dc2626' : ticket.priority === 'medium' ? '#d97706' : '#6b7280',
                    }}>
                      {priorityLabels[ticket.priority]}
                    </span>
                  </div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{ticket.title}</h4>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0.5rem 0 0', lineHeight: 1.4 }}>
                    {ticket.description}
                  </p>
                </div>
                <span style={{
                  ...sectionStyles.badge,
                  background: ticket.status === 'resolved' || ticket.status === 'closed' ? '#dcfce7' : ticket.status === 'in-progress' ? '#dbeafe' : 'var(--bg-light)',
                  color: ticket.status === 'resolved' || ticket.status === 'closed' ? '#22c55e' : ticket.status === 'in-progress' ? '#3b82f6' : 'var(--text-secondary)',
                }}>
                  {statusLabels[ticket.status]}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                <span>{t('projects.tickets.reportedBy')}: {ticket.reporter}</span>
                {ticket.assignee && <span>{t('projects.tickets.assignedTo')}: {ticket.assignee}</span>}
                <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function ProofsSection({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingProof, setEditingProof] = useState<Proof | null>(null);
  const proofs = MOCK_PROOFS;

  const statusColors: Record<string, string> = { pending: '#f59e0b', approved: '#22c55e', rejected: '#dc2626', 'changes-requested': '#8b5cf6' };
  const statusLabels: Record<string, string> = { pending: t('projects.proofs.pending'), approved: t('projects.proofs.approved'), rejected: t('projects.proofs.rejected'), 'changes-requested': t('projects.proofs.changesRequested') };

  const handleProofClick = (proof: Proof) => {
    setEditingProof(proof);
    setShowForm(true);
  };

  const handleAddProof = () => {
    setEditingProof(null);
    setShowForm(true);
  };

  const handleSaveProof = (data: ProofFormData) => {
    console.log('Save proof:', data);
    setShowForm(false);
    setEditingProof(null);
  };

  const handleDeleteProof = (id: string) => {
    console.log('Delete proof:', id);
    setShowForm(false);
    setEditingProof(null);
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <Eye size={18} />
          {t('projects.sections.proofs')}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({proofs.length})</span>
        </div>
        <button onClick={handleAddProof} style={{ ...sectionStyles.button, ...sectionStyles.buttonPrimary }}>
          <Plus size={16} /> {t('projects.proofs.uploadProof')}
        </button>
      </div>

      {/* Proofs List */}
      <div style={{ padding: '1rem' }}>
        {proofs.length === 0 ? (
          <div style={sectionStyles.emptyState}>
            <Eye size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>{t('projects.proofs.noProofs')}</h3>
            <p style={{ maxWidth: '300px' }}>{t('projects.proofs.noProofsDescription')}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {proofs.map(proof => (
              <div
                key={proof.id}
                onClick={() => handleProofClick(proof)}
                style={{
                  ...sectionStyles.card,
                  marginBottom: 0,
                  cursor: 'pointer',
                  borderTop: `4px solid ${statusColors[proof.status]}`,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-light)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg-base)')}
              >
                {/* Preview area */}
                <div style={{
                  height: '120px',
                  background: 'var(--bg-light)',
                  borderRadius: '0.375rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1rem',
                }}>
                  <FileText size={40} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{proof.title}</h4>
                    {proof.description && (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '0.25rem 0 0' }}>
                        {proof.description}
                      </p>
                    )}
                  </div>
                  <span style={{
                    ...sectionStyles.badge,
                    background: `${statusColors[proof.status]}20`,
                    color: statusColors[proof.status],
                  }}>
                    {statusLabels[proof.status]}
                  </span>
                </div>

                {proof.fileName && (
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    background: 'var(--bg-light)',
                    borderRadius: '0.375rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.8125rem',
                  }}>
                    <FileText size={14} />
                    {proof.fileName}
                  </div>
                )}

                <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Revisori:</span>
                  {proof.reviewers.map((reviewer, i) => (
                    <span key={i} style={{
                      ...sectionStyles.badge,
                      background: 'var(--bg-light)',
                      color: 'var(--text-secondary)',
                    }}>
                      {reviewer}
                    </span>
                  ))}
                </div>

                <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {t('projects.proofs.uploadedBy', { name: proof.uploadedBy, date: new Date(proof.uploadedAt).toLocaleDateString(undefined) })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Proof Form Widget */}
      <ProofWidget
        isOpen={showForm}
        proofId={editingProof?.id}
        initialData={editingProof ? {
          title: editingProof.title,
          description: editingProof.description,
          status: editingProof.status,
          reviewers: editingProof.reviewers,
        } : undefined}
        onClose={() => { setShowForm(false); setEditingProof(null); }}
        onSave={handleSaveProof}
        onDelete={editingProof ? handleDeleteProof : undefined}
      />
    </div>
  );
}

export function FormsSection({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingForm, setEditingForm] = useState<CustomForm | null>(null);
  const forms = MOCK_FORMS;

  const statusColors: Record<string, string> = { draft: '#6b7280', active: '#22c55e', closed: '#dc2626' };
  const statusLabels: Record<string, string> = { draft: t('projects.forms.draft'), active: t('projects.forms.active'), closed: t('projects.forms.closed') };

  const handleFormClick = (form: CustomForm) => {
    setEditingForm(form);
    setShowForm(true);
  };

  const handleAddForm = () => {
    setEditingForm(null);
    setShowForm(true);
  };

  const handleSaveForm = (data: CustomFormData) => {
    console.log('Save form:', data);
    setShowForm(false);
    setEditingForm(null);
  };

  const handleDeleteForm = (id: string) => {
    console.log('Delete form:', id);
    setShowForm(false);
    setEditingForm(null);
  };

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.header}>
        <div style={sectionStyles.headerTitle}>
          <FileText size={18} />
          {t('projects.sections.forms')}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({forms.length})</span>
        </div>
        <button onClick={handleAddForm} style={{ ...sectionStyles.button, ...sectionStyles.buttonPrimary }}>
          <Plus size={16} /> {t('projects.forms.newForm')}
        </button>
      </div>

      {/* Forms List */}
      <div style={{ padding: '1rem' }}>
        {forms.length === 0 ? (
          <div style={sectionStyles.emptyState}>
            <FileText size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>{t('projects.forms.noForms')}</h3>
            <p style={{ maxWidth: '300px' }}>{t('projects.forms.noFormsDescription')}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {forms.map(form => (
              <div
                key={form.id}
                onClick={() => handleFormClick(form)}
                style={{
                  ...sectionStyles.card,
                  marginBottom: 0,
                  cursor: 'pointer',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--brand)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--line)')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FileText size={20} style={{ color: 'var(--brand)' }} />
                    <h4 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{form.title}</h4>
                  </div>
                  <span style={{
                    ...sectionStyles.badge,
                    background: `${statusColors[form.status]}20`,
                    color: statusColors[form.status],
                  }}>
                    {statusLabels[form.status]}
                  </span>
                </div>

                {form.description && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '0 0 1rem', lineHeight: 1.4 }}>
                    {form.description}
                  </p>
                )}

                <div style={{
                  display: 'flex',
                  gap: '1.5rem',
                  padding: '0.75rem',
                  background: 'var(--bg-light)',
                  borderRadius: '0.375rem',
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--brand)' }}>{form.fieldsCount}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Campi</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--brand)' }}>{form.responsesCount}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('projects.forms.responses')}</div>
                  </div>
                </div>

                <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {t('projects.forms.createdBy', { name: form.createdBy, date: new Date(form.createdAt).toLocaleDateString(undefined) })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Builder Widget */}
      <FormBuilderWidget
        isOpen={showForm}
        formId={editingForm?.id}
        initialData={editingForm ? {
          title: editingForm.title,
          description: editingForm.description,
          fields: [], // In a real app, we'd load the actual fields
        } : undefined}
        onClose={() => { setShowForm(false); setEditingForm(null); }}
        onSave={handleSaveForm}
        onDelete={editingForm ? handleDeleteForm : undefined}
      />
    </div>
  );
}
